import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  ArrowLeft, 
  Wand2,
  RefreshCw,
  Save,
  AlertTriangle,
  Check,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASK_OPTIONS = [
  { value: "Keeps", label: "Keept", color: "blue", short: "K" },
  { value: "KeeperBuddy", label: "K-Buddy", color: "yellow", short: "KB" },
  { value: "Drives", label: "Rijdt", color: "amber", short: "R" },
  { value: "Referees", label: "Fluit", color: "purple", short: "F" },
  { value: "Coaches", label: "Coacht", color: "emerald", short: "C" },
];

const ACTIVITY_TASK_OPTIONS = [
  { value: "Activity", label: "Activiteit", color: "indigo", short: "A" },
  { value: "Drives", label: "Rijdt", color: "amber", short: "R" },
];

const TASK_COLORS = {
  Keeps: { bg: "bg-blue-500", text: "text-blue-700", light: "bg-blue-50", border: "border-blue-200" },
  KeeperBuddy: { bg: "bg-yellow-400", text: "text-yellow-800", light: "bg-yellow-50", border: "border-yellow-200" },
  Drives: { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-50", border: "border-amber-200" },
  Referees: { bg: "bg-purple-500", text: "text-purple-700", light: "bg-purple-50", border: "border-purple-200" },
  Coaches: { bg: "bg-emerald-500", text: "text-emerald-700", light: "bg-emerald-50", border: "border-emerald-200" },
  Coacht: { bg: "bg-emerald-500", text: "text-emerald-700", light: "bg-emerald-50", border: "border-emerald-200" },
  Rijdt: { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-50", border: "border-amber-200" },
  Activity: { bg: "bg-indigo-500", text: "text-indigo-700", light: "bg-indigo-50", border: "border-indigo-200" },
  Unavailable: { bg: "bg-red-500", text: "text-red-700", light: "bg-red-50", border: "border-red-200" },
};

// Parse task string to array
const parseTasks = (taskString) => {
  if (!taskString || taskString === "-" || taskString === "Unavailable") return [];
  return taskString.split(", ").filter(t => t && t !== "-");
};

const TASK_SHORT = {
  Keeps: "K", KeeperBuddy: "KB", Drives: "R", Referees: "F", Coaches: "C", Coacht: "C", Rijdt: "R", Activity: "A",
};

// Convert task array to string
const tasksToString = (tasks) => {
  if (tasks.length === 0) return "-";
  return tasks.join(", ");
};

// Get display for task cell
const TaskDisplay = ({ tasks, isUnavailable }) => {
  if (isUnavailable) {
    return (
      <div className="flex items-center justify-center gap-0.5">
        <div className="w-full h-7 rounded bg-red-500 flex items-center justify-center">
          <span className="text-xs text-white font-medium">N/A</span>
        </div>
      </div>
    );
  }
  
  if (tasks.length === 0) {
    return (
      <div className="flex items-center justify-center">
        <span className="text-slate-400 text-xs">–</span>
      </div>
    );
  }
  
  if (tasks.length === 1) {
    const task = tasks[0];
    const colors = TASK_COLORS[task] || TASK_COLORS.Keeps;
    return (
      <div className={cn("w-full h-7 rounded flex items-center justify-center", colors.bg)}>
        <span className="text-xs font-medium text-white">
          {TASK_SHORT[task] || task.charAt(0)}
        </span>
      </div>
    );
  }
  
  // Multiple tasks - show colored dots/segments
  return (
    <div className="flex items-center justify-center gap-0.5 h-7">
      {tasks.map((task, idx) => {
        const colors = TASK_COLORS[task] || TASK_COLORS.Keeps;
        return (
          <div 
            key={task}
            className={cn(
              "h-6 flex items-center justify-center rounded-sm",
              colors.bg,
              tasks.length === 2 ? "w-1/2 px-1" : "flex-1 px-0.5"
            )}
            title={task}
          >
            <span className="text-[10px] font-semibold text-white">
              {TASK_SHORT[task] || task.charAt(0)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Task selector popover with checkboxes
const TaskSelector = ({ matchId, playerId, tasks, isUnavailable, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const [localTasks, setLocalTasks] = useState(tasks);
  const [localUnavailable, setLocalUnavailable] = useState(isUnavailable);
  const taskOptions = options || TASK_OPTIONS;

  const handleTaskToggle = (taskValue, checked) => {
    let newTasks;
    if (checked) {
      newTasks = [...localTasks, taskValue];
    } else {
      newTasks = localTasks.filter(t => t !== taskValue);
    }
    setLocalTasks(newTasks);
    
    // If marking unavailable, clear other tasks
    if (taskValue === "Unavailable") {
      return; // Handled separately
    }
  };

  const handleUnavailableToggle = (checked) => {
    setLocalUnavailable(checked);
    if (checked) {
      setLocalTasks([]);
    }
  };

  const handleApply = () => {
    if (localUnavailable) {
      onChange("Unavailable");
    } else {
      onChange(tasksToString(localTasks));
    }
    setOpen(false);
  };

  // Reset local state when popover opens
  const handleOpenChange = (newOpen) => {
    if (newOpen) {
      setLocalTasks(tasks);
      setLocalUnavailable(isUnavailable);
    }
    setOpen(newOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="w-full h-8 px-1 rounded border border-transparent hover:border-slate-300 transition-colors flex items-center justify-between gap-1 group"
          data-testid={`task-select-${matchId}-${playerId}`}
        >
          <div className="flex-1">
            <TaskDisplay tasks={tasks} isUnavailable={isUnavailable} />
          </div>
          <ChevronDown className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="start">
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Taken toewijzen</p>
          
          {taskOptions.map(option => {
            const colors = TASK_COLORS[option.value];
            const isChecked = localTasks.includes(option.value);
            return (
              <label
                key={option.value}
                className={cn(
                  "flex items-center gap-2 p-2 rounded cursor-pointer transition-colors",
                  isChecked ? colors.light : "hover:bg-slate-50",
                  localUnavailable && "opacity-50 pointer-events-none"
                )}
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={(checked) => handleTaskToggle(option.value, checked)}
                  disabled={localUnavailable}
                  className={cn(isChecked && colors.border)}
                />
                <div className={cn("w-5 h-5 rounded flex items-center justify-center", colors.bg)}>
                  <span className="text-white text-xs font-bold">{option.short}</span>
                </div>
                <span className="text-sm text-slate-700">{option.label}</span>
              </label>
            );
          })}
          
          <div className="border-t border-slate-100 pt-2 mt-2">
            <label
              className={cn(
                "flex items-center gap-2 p-2 rounded cursor-pointer transition-colors",
                localUnavailable ? TASK_COLORS.Unavailable.light : "hover:bg-slate-50"
              )}
            >
              <Checkbox
                checked={localUnavailable}
                onCheckedChange={handleUnavailableToggle}
              />
              <div className="w-5 h-5 rounded bg-red-500 flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">N/A</span>
              </div>
              <span className="text-sm text-slate-700">Afwezig</span>
            </label>
          </div>
          
          <Button 
            size="sm" 
            className="w-full mt-2 bg-slate-900 hover:bg-slate-800"
            onClick={handleApply}
          >
            <Check className="w-3 h-3 mr-1" />
            Toepassen
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const Planning = () => {
  const { seasonPartId: urlSeasonPartId } = useParams();
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [seasonPartId, setSeasonPartId] = useState(urlSeasonPartId);
  const [seasonPart, setSeasonPart] = useState(null);
  const [matches, setMatches] = useState([]);
  const [activities, setActivities] = useState([]);
  const [players, setPlayers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [planning, setPlanning] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    fetchData();
  }, [urlSeasonPartId]);

  const fetchData = async () => {
    try {
      // Get season
      const seasonsRes = await axios.get(`${API}/seasons`);
      if (seasonsRes.data.length === 0) {
        toast.error("Geen seizoen gevonden");
        navigate("/season");
        return;
      }
      
      const currentSeason = seasonsRes.data[0];
      setSeason(currentSeason);
      
      // Use URL param or default to first season part
      const partId = urlSeasonPartId || currentSeason.season_parts?.[0]?.id;
      if (!partId) {
        toast.error("Geen seizoensdelen gevonden");
        navigate("/season");
        return;
      }
      setSeasonPartId(partId);
      
      const part = currentSeason.season_parts?.find(p => p.id === partId);
      setSeasonPart(part);
      
      // Get matches and players
      const [matchesRes, playersRes, planningRes, membersRes, activitiesRes] = await Promise.all([
        axios.get(`${API}/matches?season_id=${currentSeason.id}&season_part_id=${partId}`),
        axios.get(`${API}/players?season_id=${currentSeason.id}`),
        axios.get(`${API}/planning?season_id=${currentSeason.id}&season_part_id=${partId}`),
        axios.get(`${API}/team/members`),
        axios.get(`${API}/activities?season_id=${currentSeason.id}`),
      ]);
      
      // Sort matches by date
      const sortedMatches = matchesRes.data.sort((a, b) => a.date.localeCompare(b.date));
      setMatches(sortedMatches);
      setActivities(activitiesRes.data);
      setPlayers(playersRes.data.sort((a, b) => (a.first_name || "").localeCompare(b.first_name || "")));
      setManagers(membersRes.data.members || []);
      setPlanning(planningRes.data);
      
    } catch (error) {
      toast.error("Gegevens laden mislukt");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePlanning = async (isRegenerate = false) => {
    if (isRegenerate && planning.length > 0) {
      setShowRegenerateDialog(true);
      return;
    }
    
    await generatePlanning();
  };

  const generatePlanning = async () => {
    setGenerating(true);
    setShowRegenerateDialog(false);
    
    try {
      await axios.post(`${API}/planning/generate`, {
        season_id: season.id,
        season_part_id: seasonPartId
      });
      
      toast.success("Planning succesvol gegenereerd!");
      
      // Reload planning data
      const planningRes = await axios.get(`${API}/planning?season_id=${season.id}&season_part_id=${seasonPartId}`);
      setPlanning(planningRes.data);
      setPendingChanges({});
      setHasChanges(false);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Planning genereren mislukt");
    } finally {
      setGenerating(false);
    }
  };

  const getPlayerTaskString = (matchId, playerId) => {
    // Check pending changes first
    const changeKey = `${matchId}|${playerId}`;
    if (pendingChanges[changeKey] !== undefined) {
      return pendingChanges[changeKey];
    }
    
    // Find in planning
    const matchPlanning = planning.find(p => p.match_id === matchId);
    if (!matchPlanning) return "-";
    
    const assignment = matchPlanning.assignments?.find(a => a.player_id === playerId);
    return assignment?.task || "-";
  };

  const getManagerTaskString = (matchId, userId) => {
    const changeKey = `${matchId}|${userId}`;
    if (pendingChanges[changeKey] !== undefined) {
      return pendingChanges[changeKey];
    }
    const matchPlanning = planning.find(p => p.match_id === matchId);
    if (!matchPlanning) return "-";
    const ma = matchPlanning.manager_assignments?.find(a => a.user_id === userId);
    return ma?.task || "-";
  };

  const handleTaskChange = (matchId, playerId, newTaskString) => {
    const changeKey = `${matchId}|${playerId}`;
    setPendingChanges(prev => ({
      ...prev,
      [changeKey]: newTaskString
    }));
    setHasChanges(true);
    setWarnings([]);
  };

  const validatePlanning = (planningData, matchesData, playersData) => {
    const w = [];

    for (const match of matchesData) {
      const mp = planningData.find(p => p.match_id === match.id);
      if (!mp) continue;

      const assignments = mp.assignments || [];
      const taskMap = {};
      for (const a of assignments) {
        if (a.task === "-" || a.task === "Unavailable") continue;
        const tasks = a.task.split(", ").map(t => t.trim());
        for (const t of tasks) {
          if (!taskMap[t]) taskMap[t] = [];
          taskMap[t].push(a.player_id);
        }
      }

      const label = `${new Date(match.date || match.match_date).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })} ${match.opponent_club}`;

      // Rule 1: Exactly 1 Keept per match
      const keeptCount = (taskMap["Keeps"] || []).length;
      if (keeptCount === 0) w.push({ match: label, msg: "Geen keeper toegewezen" });
      else if (keeptCount > 1) w.push({ match: label, msg: `${keeptCount} keepers toegewezen (verwacht: 1)` });

      // Rule 2: Fluit only for home matches, exactly 2
      const refCount = (taskMap["Referees"] || []).length;
      if (match.is_home) {
        if (refCount === 0) w.push({ match: label, msg: "Geen fluiters toegewezen (thuiswedstrijd)" });
        else if (refCount !== 2) w.push({ match: label, msg: `${refCount} fluiter(s) toegewezen (verwacht: 2)` });
      }

      // Rule 3: Drives for away matches only, exactly 2 players (managers drive separately)
      const playerDriveCount = (taskMap["Drives"] || []).length;
      if (!match.is_home) {
        if (playerDriveCount === 0) w.push({ match: label, msg: "Geen rijders toegewezen (uitwedstrijd, verwacht: 2 spelers)" });
        else if (playerDriveCount !== 2) w.push({ match: label, msg: `${playerDriveCount} speler-rijder(s) toegewezen (verwacht: 2)` });
      } else {
        if (playerDriveCount > 0) w.push({ match: label, msg: `${playerDriveCount} rijder(s) toegewezen (niet nodig bij thuiswedstrijd)` });
      }
    }
    return w;
  };

  const handleSaveChanges = async () => {
    setSaving(true);

    try {
      // Save all pending changes
      for (const [key, task] of Object.entries(pendingChanges)) {
        const [matchId, playerId] = key.split('|');
        try {
          await axios.put(`${API}/planning/update`, {
            match_id: matchId,
            player_id: playerId,
            task: task,
            is_manual_edit: true
          });
        } catch (innerErr) {
          // Surface the actual backend detail so the user (and we) can diagnose.
          const detail = innerErr.response?.data?.detail || innerErr.message || "onbekende fout";
          const status = innerErr.response?.status || "?";
          throw new Error(`[${status}] ${detail} (match=${matchId}, player=${playerId})`);
        }
      }

      toast.success("Wijzigingen opgeslagen!");
      setPendingChanges({});
      setHasChanges(false);
      
      // Reload planning
      const planningRes = await axios.get(`${API}/planning?season_id=${season.id}&season_part_id=${seasonPartId}`);
      setPlanning(planningRes.data);
      
      // Validate against business rules
      const w = validatePlanning(planningRes.data, matches, players);
      setWarnings(w);
      
    } catch (error) {
      toast.error(`Opslaan mislukt: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  const hasPlanningData = planning.length > 0;

  // Build unified rows: matches + activities sorted by date
  const unifiedRows = (() => {
    const matchRows = matches.map(m => ({ type: "match", id: m.id, date: m.date, ...m }));
    const activityRows = activities.map(a => ({ type: "activity", id: a.id, date: a.date, description: a.description, time: a.time }));
    return [...matchRows, ...activityRows].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  })();

  const handleSeasonPartChange = (partId) => {
    setSeasonPartId(partId);
    navigate(`/planning/${partId}`);
  };

  return (
    <div data-testid="planning-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-blue-600 mb-1">
            {season?.club_name} / {season?.team_name}
          </p>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Planning
          </h1>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-slate-500 mt-1 -ml-2 h-auto py-1 px-2">
                {seasonPart?.name || "Selecteer seizoensdeel"}
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {season?.season_parts?.map(part => (
                <DropdownMenuItem 
                  key={part.id}
                  onClick={() => handleSeasonPartChange(part.id)}
                  className={cn(part.id === seasonPartId && "bg-slate-100")}
                >
                  {part.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex gap-2">
          {hasPlanningData && (
            <Button
              variant="outline"
              onClick={() => handleGeneratePlanning(true)}
              disabled={generating}
              data-testid="regenerate-planning-btn"
            >
              <RefreshCw className={cn("w-4 h-4 mr-1", generating && "animate-spin")} />
              Opnieuw genereren
            </Button>
          )}
          {!hasPlanningData && (
            <Button
              onClick={() => handleGeneratePlanning(false)}
              disabled={generating || matches.length === 0 || players.length === 0}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="generate-planning-btn"
            >
              {generating ? (
                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4 mr-1" />
              )}
              Planning Genereren
            </Button>
          )}
          {hasChanges && (
            <Button
              onClick={handleSaveChanges}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-changes-btn"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Wijzigingen Opslaan
            </Button>
          )}
        </div>
      </div>

      {/* Info/Warning Messages */}
      {matches.length === 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <p className="text-amber-800">Nog geen wedstrijden gepland. Voeg eerst wedstrijden toe voordat je een planning genereert.</p>
          </CardContent>
        </Card>
      )}

      {players.length === 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <p className="text-amber-800">Nog geen spelers toegevoegd. Voeg eerst spelers toe voordat je een planning genereert.</p>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-slate-500">Legenda:</span>
        {[...TASK_OPTIONS, ...ACTIVITY_TASK_OPTIONS.filter(o => !TASK_OPTIONS.some(t => t.value === o.value))].map(task => {
          const colors = TASK_COLORS[task.value];
          return (
            <div key={task.value} className="flex items-center gap-1.5">
              <div className={cn("w-5 h-5 rounded flex items-center justify-center", colors.bg)}>
                <span className="text-white text-xs font-bold">{task.short}</span>
              </div>
              <span className="text-xs text-slate-600">{task.label}</span>
            </div>
          );
        })}
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded bg-red-500 flex items-center justify-center">
            <span className="text-white text-[10px] font-bold">N/A</span>
          </div>
          <span className="text-xs text-slate-600">Afwezig</span>
        </div>
      </div>

      {/* Planning Table */}
      {(hasPlanningData || activities.length > 0) ? (
        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
          <table className="w-full bg-white" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap bg-slate-50" style={{ position: "sticky", left: 0, zIndex: 20, minWidth: 88 }}>Datum</th>
                <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap bg-slate-50 sticky-col-plan-tu-header" style={{ minWidth: 56 }}>T/U</th>
                <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap bg-slate-50 border-r border-slate-200 sticky-col-plan-teg-header" style={{ minWidth: 140 }}>Tegenstander</th>
                {players.map(player => {
                  const label = (player.last_name || player.first_name || "").trim();
                  const short = label.length > 8 ? `${label.substring(0, 8)}…` : label;
                  return (
                    <th
                      key={player.id}
                      className="text-center text-xs uppercase font-medium text-slate-500 tracking-wider px-1 py-3 whitespace-nowrap"
                      style={{ minWidth: 72, maxWidth: 72, width: 72 }}
                      title={`${player.first_name || ""} ${player.last_name || ""}`.trim()}
                    >
                      {short}
                    </th>
                  );
                })}
                {managers.length > 0 && (
                  <th className="text-center text-xs uppercase font-medium text-slate-400 tracking-wider px-1 py-3 whitespace-nowrap border-l-2 border-slate-300" style={{ minWidth: 4, maxWidth: 4, width: 4 }}></th>
                )}
                {managers.map(mgr => {
                  const full = (mgr.name || mgr.email || "").trim();
                  // Prefer last_name to match the player-column convention. Falls back to
                  // first_name and then the first token of `name` for legacy accounts.
                  const label = (mgr.last_name || mgr.first_name || full.split(" ")[0] || "").trim();
                  const short = label.length > 8 ? `${label.substring(0, 8)}…` : label;
                  return (
                    <th
                      key={mgr.user_id}
                      className="text-center text-xs uppercase font-medium text-emerald-600 tracking-wider px-1 py-3 whitespace-nowrap"
                      style={{ minWidth: 72, maxWidth: 72, width: 72 }}
                      title={full}
                    >
                      {short}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {unifiedRows.map((row, index) => {
                if (row.type === "activity") {
                  // Activity row - description spans T/U + Tegenstander columns
                  const hasPlanningEntry = planning.some(p => p.match_id === row.id);
                  return (
                    <tr key={row.id} data-testid={`planning-row-${index}`} style={{ backgroundColor: "#f5f3ff" }}>
                      <td className="px-3 py-2 text-sm text-slate-700 whitespace-nowrap" style={{ position: "sticky", left: 0, zIndex: 10, backgroundColor: "#f5f3ff", minWidth: 88 }}>
                        {row.date ? format(new Date(row.date), "dd MMM") : "-"}
                      </td>
                      <td colSpan={2} className="px-3 py-2 text-sm font-medium text-indigo-700 whitespace-nowrap truncate border-r border-slate-100 sticky-col-plan-tu sticky-col-plan-teg" style={{ backgroundColor: "#f5f3ff" }}>
                        {row.description}
                        {row.time && <span className="ml-2 text-xs font-normal text-indigo-400">{row.time}</span>}
                      </td>
                      {hasPlanningEntry ? (
                        <>
                          {players.map(player => {
                            const taskString = getPlayerTaskString(row.id, player.id);
                            const isUnavailable = taskString === "Unavailable";
                            const tasks = parseTasks(taskString);
                            return (
                              <td key={player.id} className="px-1 py-2" style={{ minWidth: 72, maxWidth: 72, width: 72, backgroundColor: "#f5f3ff" }}>
                                <TaskSelector
                                  matchId={row.id}
                                  playerId={player.id}
                                  tasks={tasks}
                                  isUnavailable={isUnavailable}
                                  onChange={(newTaskString) => handleTaskChange(row.id, player.id, newTaskString)}
                                  options={ACTIVITY_TASK_OPTIONS}
                                />
                              </td>
                            );
                          })}
                          {managers.length > 0 && (
                            <td className="border-l-2 border-slate-300" style={{ minWidth: 4, maxWidth: 4, width: 4, backgroundColor: "#f5f3ff" }}></td>
                          )}
                          {managers.map(mgr => {
                            const taskString = getManagerTaskString(row.id, mgr.user_id);
                            const isUnavailable = taskString === "Unavailable";
                            const tasks = parseTasks(taskString);
                            return (
                              <td key={mgr.user_id} className="px-1 py-2" style={{ minWidth: 72, maxWidth: 72, width: 72, backgroundColor: "#f5f3ff" }}>
                                <TaskSelector
                                  matchId={row.id}
                                  playerId={mgr.user_id}
                                  tasks={tasks}
                                  isUnavailable={isUnavailable}
                                  onChange={(newTaskString) => handleTaskChange(row.id, mgr.user_id, newTaskString)}
                                  options={ACTIVITY_TASK_OPTIONS}
                                />
                              </td>
                            );
                          })}
                        </>
                      ) : (
                        <td colSpan={players.length + managers.length + (managers.length > 0 ? 1 : 0)} className="px-3 py-2 text-xs text-slate-400 text-center" style={{ backgroundColor: "#f5f3ff" }}>
                          Genereer planning om taken toe te wijzen
                        </td>
                      )}
                    </tr>
                  );
                }

                // Match row (existing logic)
                const match = row;
                const bg = match.is_home ? "#ffffff" : "#f8fafc";
                return (
                  <tr key={match.id} className={match.is_home ? "bg-white" : "bg-slate-50"} data-testid={`planning-row-${index}`}>
                    <td className="px-3 py-2 text-sm text-slate-700 whitespace-nowrap" style={{ position: "sticky", left: 0, zIndex: 10, backgroundColor: bg, minWidth: 88 }}>
                      {match.date ? format(new Date(match.date), "dd MMM") : "-"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap sticky-col-plan-tu" style={{ backgroundColor: bg, minWidth: 56 }}>
                      <span className={cn("text-xs font-medium", match.is_home ? "text-emerald-700" : "text-amber-700")}>
                        {match.is_home ? "Thuis" : "Uit"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700 whitespace-nowrap truncate border-r border-slate-100 sticky-col-plan-teg" style={{ backgroundColor: bg, minWidth: 140, maxWidth: 140 }}>
                      {match.opponent_club}
                    </td>
                    {players.map(player => {
                      const taskString = getPlayerTaskString(match.id, player.id);
                      const isUnavailable = taskString === "Unavailable";
                      const tasks = parseTasks(taskString);
                      
                      return (
                        <td key={player.id} className="px-1 py-2" style={{ minWidth: 72, maxWidth: 72, width: 72 }}>
                          <TaskSelector
                            matchId={match.id}
                            playerId={player.id}
                            tasks={tasks}
                            isUnavailable={isUnavailable}
                            onChange={(newTaskString) => handleTaskChange(match.id, player.id, newTaskString)}
                          />
                        </td>
                      );
                    })}
                    {managers.length > 0 && (
                      <td className="border-l-2 border-slate-300" style={{ minWidth: 4, maxWidth: 4, width: 4 }}></td>
                    )}
                    {managers.map(mgr => {
                      const taskString = getManagerTaskString(match.id, mgr.user_id);
                      const isUnavailable = taskString === "Unavailable";
                      const tasks = parseTasks(taskString);
                      return (
                        <td key={mgr.user_id} className="px-1 py-2" style={{ minWidth: 72, maxWidth: 72, width: 72 }}>
                          <TaskSelector
                            matchId={match.id}
                            playerId={mgr.user_id}
                            tasks={tasks}
                            isUnavailable={isUnavailable}
                            onChange={(newTaskString) => handleTaskChange(match.id, mgr.user_id, newTaskString)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Warnings panel */}
          {warnings.length > 0 && (
            <Card className="bg-amber-50 border border-amber-200 rounded-xl shadow-sm mt-4" data-testid="planning-warnings">
              <CardContent className="p-4">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-semibold text-amber-800">
                    Planning wijkt af van de bedrijfsregels ({warnings.length} {warnings.length === 1 ? "waarschuwing" : "waarschuwingen"})
                  </p>
                </div>
                <ul className="space-y-1 ml-7">
                  {warnings.map((w, i) => (
                    <li key={i} className="text-sm text-amber-700">
                      <span className="font-medium">{w.match}:</span> {w.msg}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

        </div>
      ) : (
        matches.length > 0 && players.length > 0 && (
          <Card className="bg-white border border-slate-200 rounded-xl shadow-sm">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wand2 className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Nog geen planning gegenereerd</h3>
              <p className="text-slate-500 mb-6">Klik op "Planning Genereren" om automatisch taken aan spelers toe te wijzen</p>
              <Button 
                onClick={() => handleGeneratePlanning(false)}
                disabled={generating}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="empty-generate-btn"
              >
                {generating ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Wand2 className="w-4 h-4 mr-2" />
                )}
                Planning Genereren
              </Button>
            </CardContent>
          </Card>
        )
      )}

      {/* Task Distribution Summary */}
      {hasPlanningData && (
        <Card className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <CardHeader className="border-b border-slate-100 py-3 px-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700" style={{ fontFamily: 'var(--font-display)' }}>
              Taakverdeling
            </h3>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {players.map(player => {
                const tasks = { Keeps: 0, Referees: 0, Drives: 0 };
                
                matches.forEach(match => {
                  const taskString = getPlayerTaskString(match.id, player.id);
                  if (taskString && taskString !== "-" && taskString !== "Unavailable") {
                    if (taskString.includes("Keeps")) tasks.Keeps++;
                    if (taskString.includes("Referees")) tasks.Referees++;
                    if (taskString.includes("Drives")) tasks.Drives++;
                  }
                });
                
                return (
                  <div key={player.id} className="bg-slate-50 rounded-lg p-3">
                    <p className="text-sm font-medium text-slate-900 truncate">{player.first_name} {player.last_name?.charAt(0)}.</p>
                    <div className="flex gap-3 mt-2">
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-blue-500 flex items-center justify-center">
                          <span className="text-white text-[9px] font-bold">K</span>
                        </div>
                        <span className="text-xs text-slate-600">{tasks.Keeps}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-purple-500 flex items-center justify-center">
                          <span className="text-white text-[9px] font-bold">F</span>
                        </div>
                        <span className="text-xs text-slate-600">{tasks.Referees}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-amber-500 flex items-center justify-center">
                          <span className="text-white text-[9px] font-bold">R</span>
                        </div>
                        <span className="text-xs text-slate-600">{tasks.Drives}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regenerate Confirmation Dialog */}
      <AlertDialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Planning opnieuw genereren?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je de huidige planning wilt vervangen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-regenerate-btn">Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={generatePlanning}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="confirm-regenerate-btn"
            >
              Ja, genereer opnieuw
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
